import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { queryClient, trpc } from "../../app/trpc.js";
import { Button, Card, Input, Label, Notice, PageTitle, Spinner } from "../../ui/index.js";
import { useCabinet } from "../cabinet/index.js";
import { LeaveGuard } from "./LeaveGuard.js";
import { RulesEditor } from "./steps/RulesStep.js";
import type { OnboardingState } from "./types.js";

/** Код шаблона «Другое» (как `OTHER_TEMPLATE` на сервере). */
const OTHER = "other";

/**
 * «Данные салона и правила» в «Ещё» (решение 04.11.2026: после мастера настройки всё, что настраивалось по шагам,
 * живёт на отдельных страницах). Та же форма, что на шаге правил (`RulesEditor`): адрес, строка о часах, ответы на
 * вопросы шаблона ниши, поздняя отмена, опоздание, языки, темы для мастера, FAQ. Тип салона здесь не меняется
 * (это заменило бы черновик услуг и вопросы); у «Другое» можно поправить своё название типа.
 * Уход с правками спрашивает подтверждение, закрытие вкладки предупреждает браузер.
 */
export function SalonProfilePage() {
  const { t } = useTranslation();
  const { salonId } = useCabinet();
  const key = trpc.onboarding.state.queryKey({ salonId });
  const state = useQuery(trpc.onboarding.state.queryOptions({ salonId }, { enabled: Boolean(salonId) }));
  const [rulesDirty, setRulesDirty] = useState(false);
  const [nicheName, setNicheName] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const s = state.data;
  const other = s?.templateCode === OTHER;
  const niche = other ? (nicheName ?? s?.nicheName ?? "") : undefined;
  const dirty = rulesDirty || (other && nicheName !== null && nicheName.trim() !== (s?.nicheName ?? ""));

  if (state.isPending) return <Spinner />;
  if (state.isError || !s) return <Notice kind="error">{t("common.error")}</Notice>;
  return (
    <div className="lg:max-w-3xl">
      <PageTitle>{t("salonProfile.title")}</PageTitle>
      <p className="mb-4 text-slate-600">{t("salonProfile.intro")}</p>
      <Card className="mb-4 space-y-2">
        <div className="text-sm text-slate-500">{t("salonProfile.type")}</div>
        <div className="font-medium">{s.templateCode ? t(`onb.niche.${s.templateCode}`) : t("salonProfile.noType")}</div>
        {other && (
          <div>
            <Label htmlFor="sp-niche">{t("onb.template.otherName")}</Label>
            <Input
              id="sp-niche"
              value={niche}
              maxLength={60}
              onChange={(e) => {
                setSaved(false);
                setNicheName(e.target.value);
              }}
              autoComplete="off"
            />
          </div>
        )}
      </Card>
      <RulesEditor
        salonId={salonId}
        state={s}
        nicheName={niche}
        onDirty={(d) => {
          if (d) setSaved(false);
          setRulesDirty(d);
        }}
        onSaved={(next: OnboardingState) => {
          queryClient.setQueryData(key, next);
          setNicheName(null);
          setSaved(true);
        }}
        actions={({ valid, saving, save }) => (
          <div className="mt-6 space-y-2">
            {saved && <Notice kind="success">{t("settings.saved")}</Notice>}
            <Button className="w-full lg:w-auto lg:px-8" disabled={!valid || saving} onClick={save}>
              {saving ? t("common.loading") : t("settings.save")}
            </Button>
          </div>
        )}
      />
      <LeaveGuard blockRoutes={dirty} blockUnload={dirty} />
    </div>
  );
}
